import { useCallback, useState } from 'react';

import { Grid, Button, Alert, Heading, Box } from '@simplywallst/ui-core';

import CrossSolid from '@simplywallst/ui-core/icons/CrossSolid';

import { pluralise } from '@/utilities/pluralise';

import { useCreateAddHoldings } from './hooks';
import { useManualPortfolioTracking } from '../../hooks/useManualPortfolioTracking';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';
import type { ManualPortfolioState } from '../CreateManualPortfolio';

import { SearchHoldingsMobile } from './SearchHoldingsMobile';

interface Props {
  portfolio: Portfolio | undefined;
  setPortfolioId: (id: string) => void;
  setManualPortfolioState: (state: ManualPortfolioState) => void;
  onClose: () => void;
}

export const AddHoldingsMobile = ({
  portfolio,
  setPortfolioId,
  setManualPortfolioState,
  onClose,
}: Props) => {
  const [holdings, setHoldings] = useState<string[]>([]);
  const [existingHolding, setExistingHolding] = useState<null | string>(null);

  const { trackManualPortfolioEvent } = useManualPortfolioTracking();

  const {
    createNewPortfolio,
    addNewHoldings,
    createNewPortfolioMutationStatus,
    addNewHoldingsMutationStatus,
  } = useCreateAddHoldings({
    onSuccess(portfolio) {
      setManualPortfolioState('edit');
      setPortfolioId(portfolio.id);
      setHoldings([]);
    },
  });

  const handleSelectHolding = useCallback(
    (selectedHolding: string) => {
      const previouslySelectedHolding = holdings.includes(selectedHolding);
      const isExistingHoldingInPortfolio = portfolio?.holdings.find(
        ({ company }) => company.uniqueSymbol === selectedHolding
      );
      if (previouslySelectedHolding) {
        return;
      }
      if (isExistingHoldingInPortfolio) {
        setExistingHolding(selectedHolding);
        return;
      }
      setHoldings((holdings) => [...holdings, selectedHolding]);
      setExistingHolding(null);
    },
    [holdings, portfolio?.holdings]
  );

  const handleRemoveHolding = useCallback((removeHolding: string) => {
    setHoldings((holdings) =>
      holdings.filter((holding) => holding !== removeHolding)
    );
  }, []);

  const handleAddHoldings = useCallback(() => {
    if (!portfolio) {
      createNewPortfolio(
        { companyUniqueSymbols: holdings },
        {
          onSuccess(newPortfolio) {
            trackManualPortfolioEvent({
              modifier: 'add-holdings',
              action: 'click',
              portfolioId: newPortfolio.id,
              holdingsCount: holdings.length,
            });
          },
        }
      );
    } else {
      trackManualPortfolioEvent({
        modifier: 'add-holdings',
        action: 'click',
        portfolioId: portfolio.id,
        holdingsCount: holdings.length,
      });
      const formattedHoldings = holdings.map((holding) => {
        const [exchangeCode, symbol] = holding.split(':');
        return {
          exchangeCode,
          symbol,
        };
      });
      addNewHoldings({ holdings: formattedHoldings, id: portfolio.id });
    }
  }, [
    addNewHoldings,
    createNewPortfolio,
    holdings,
    portfolio,
    trackManualPortfolioEvent,
  ]);

  const isLoading =
    createNewPortfolioMutationStatus === 'pending' ||
    addNewHoldingsMutationStatus === 'pending';

  const isError =
    createNewPortfolioMutationStatus === 'error' ||
    addNewHoldingsMutationStatus === 'error';

  const isFooterVisible = holdings.length > 0 || existingHolding;

  return (
    <Grid
      data-cy-id="search-add-holdings-modal"
      templateRows="min-content 1fr"
      padding="x2"
      gap="16px"
    >
      <Grid
        templateColumns="1fr min-content"
        justifyContent="space-between"
        alignContent="center"
      >
        <Heading as="h3">Add all your holdings in one go</Heading>
        <Button styleType="passive" onClick={onClose} aria-label="Close">
          <CrossSolid />
        </Button>
      </Grid>

      <SearchHoldingsMobile
        holdings={holdings}
        onRemoveHolding={handleRemoveHolding}
        onSelectHolding={handleSelectHolding}
      />

      <Grid as="footer" templateRows="max-content 1fr">
        {isFooterVisible && (
          <>
            {isError ? (
              <Box paddingY="x2" paddingXLg="x3">
                <Alert styleType="negative">
                  Unable to add holdings. Please try again later.
                </Alert>
              </Box>
            ) : null}
            {existingHolding ? (
              <Box paddingY="x2" paddingXLg="x3">
                <Alert styleType="negative">
                  {existingHolding} already exists in your portfolio. Please try
                  adding a different ticker symbol.
                </Alert>
              </Box>
            ) : null}
            <Button
              styleType="louderLarge"
              onClick={handleAddHoldings}
              isLoading={isLoading}
              disabled={isLoading || holdings.length === 0}
            >
              {holdings.length > 0
                ? `Add ${holdings.length} ${pluralise(
                    holdings.length,
                    'holding',
                    'holdings'
                  )}`
                : 'Add holdings'}
            </Button>
          </>
        )}
      </Grid>
    </Grid>
  );
};
